import axios from 'ps-components/lib/apiClient';
import { useAppState } from '../redux';
import { useQuery } from 'react-query';
import { useReducer } from 'react';
import { useDebouncedState } from '@mantine/hooks';

type FormulaLibraryState = {
    page: number
    limit: number
    search: string
    showAllSites: boolean
    selectedSite?: string
}

type FormulaLibraryAction =
    { type: 'setPage', payload: number } |
    { type: 'setLimit', payload: number } |
    { type: 'setSite', payload: string } |
    { type: 'toggleShowAllSites' }

const reducer = (state: FormulaLibraryState, action: FormulaLibraryAction): FormulaLibraryState => {
    switch (action.type) {
        case 'setPage':
            return { ...state, page: action.payload }
        case 'setLimit':
            return { ...state, limit: action.payload, page: 1 }
        case 'setSite':
            return { ...state, selectedSite: action.payload }
        case 'toggleShowAllSites':
            return { ...state, showAllSites: !state.showAllSites }
        default: return state
    }
}

export const useFormulaLibrary = () => {
    const [debouncedSearch, setDebouncedSearch] = useDebouncedState('', 500)
    const appState = useAppState()
    const subsite = appState.session.subsite
    const initialState: FormulaLibraryState = {
        page: 1,
        limit: 50,
        search: '',
        showAllSites: false,
        selectedSite: subsite
    }
    const [state, dispatch] = useReducer(reducer, initialState)
    const { showAllSites, selectedSite, page, limit } = state

    const getFormulas = async (): Promise<{ rows: Record<string, string>[], totalCount: number}> => {
        const search = debouncedSearch ? `&search=${debouncedSearch}` : ''
        const site = showAllSites ? '' : `&site=${selectedSite ?? subsite}`
        const queryParams = `?page=${page}&limit=${limit}${search}${site}`
        const response = await axios.apiGet(`/api/marketpay-internal/report-inspector/formulas${queryParams}`)
        const { formulas } = response.data
        return { rows: formulas, totalCount: formulas[0]?.total_count || 0 }
    }

    const query = useQuery(['formulas', { site: selectedSite, page, limit, search: debouncedSearch, showAllSites }], getFormulas, { keepPreviousData: true })

    const setPage = (page: number) => dispatch({ type: 'setPage', payload: page })
    const setLimit = (limit: string) => dispatch({ type: 'setLimit', payload: Number(limit) })
    const setSite = (site: string) => dispatch({ type: 'setSite', payload: site })
    const toggleShowAllSites = () => dispatch({ type: 'toggleShowAllSites' })

    return {
        query,
        state,
        actions: {
            setPage,
            setLimit,
            setDebouncedSearch,
            setSite,
            toggleShowAllSites
        }
    }
}